import React, { useState, useEffect, useCallback, MutableRefObject, useRef, useMemo } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { Calendar } from './Calendar';
import { getMoment, getFormattedDateString, getMomentDateObject, isValidDateTime } from 'utils/DateTimeUtils';

import './DateTimePicker.less';
import { debounce } from 'debounce';
import { DropDownOption } from '../DropDownOption';
import {
  validateDate,
  ValidateDateResult,
  getMomentAndDateString,
  getDateFormat,
  getMomentDateFormat,
} from 'components/inputs/date-time/utils/DateTimePickerUtils';
import { TimeSliders, ITimeSliderValue } from './TimeSliders';
import { ValidationSummary } from 'utils/ValidationUtils';
import { useTabbingContext } from 'contexts/TabbingContext';
import { ValidationWrapper } from '../Validation/ValidationWrapper';
import { IconButton } from 'components/buttons/IconButton';
import { DropDownOverlay } from '../DropDownOverlay';
import { useTrackFocus } from '../hooks/useTrackFocus';
import { convertToMomentDateFormat } from '../../../utils/DateTimeUtils';
import { Ripple } from 'components/Feedback/Ripple';
import { isNullOrWhitespace } from 'utils/StringUtils';

interface IDateTimePickerProps {
  /** Either a valid string, or else a moment object must be provided */
  currentDate?: string | moment.Moment;
  /** True if it's a a picker for both date and time */
  isDateTimePicker?: boolean;
  onChange: (date?: moment.Moment) => void;
  //need suggested date in few instances
  onSuggestionChange?: (dateText: string, suggestion?: ValidateDateResult) => void;
  /** Pass clear handling to handle clear, but also to show X */
  onClear?: () => void;
  validation?: ValidationSummary;
  autoFocus?: boolean;
  tabIndex?: number;
  className?: string;
  keepInvalidSuggestion?: boolean;
  customDateFormat?: string;
  pickerClassname?: string;
  /**
   * true if the field can be cleared and save a null value
   */
  nullable?: boolean;
  onExit?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onEnter?: (event: React.FocusEvent<HTMLInputElement>) => void;
  textBoxRef?: MutableRefObject<HTMLInputElement | null>;
  holderRef?: MutableRefObject<HTMLDivElement | null>;
  inputClassName?: string;
  iconButtonClass?: string;
  iconClass?: string;
  hideIcon?: boolean;
  hideSuggestion?: boolean;
  useRippleEffect?: boolean;
}

const getIconClassName = (active: boolean, isTimePicker?: boolean, className?: string) =>
  classNames(className, {
    'dpc-datetime-picker__date-icon': !isTimePicker,
    'dpc-datetime-picker__date-time-icon': isTimePicker,
    'dpc-datetime-picker__date-icon--active': !isTimePicker && active,
    'dpc-datetime-picker__date-time-icon--active': isTimePicker && active,
  });

const getClasses = (layout: any, isFocused?: boolean, className?: string) =>
  classNames('dpc-datetime-picker', 'dpc-text-input-wrapper', className, {
    'dpc-text-input-wrapper--focused': isFocused,
    'dpc-datetime-picker--focused': isFocused,
    'full-size': layout,
  });

const getInputClassNames = (className?: string) =>
  classNames('dpc-datetime-picker__input', 'dpc-text-input', className);

/**
 * Date and (optionally) Time picker component. Behaves like a drop down,
 * but shows a calendar instead.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function DateTimePicker({
  currentDate,
  isDateTimePicker,
  onChange,
  onClear,
  validation,
  autoFocus,
  onSuggestionChange,
  tabIndex: propsTabIndex,
  className,
  keepInvalidSuggestion,
  customDateFormat,
  nullable,
  pickerClassname,
  onExit,
  onEnter,
  holderRef,
  textBoxRef,
  inputClassName,
  iconButtonClass,
  iconClass,
  hideIcon,
  useRippleEffect,
}: IDateTimePickerProps) {
  const format = customDateFormat || getDateFormat(isDateTimePicker);
  const momentFormat = customDateFormat
    ? convertToMomentDateFormat(customDateFormat)
    : getMomentDateFormat(isDateTimePicker);
  const [isOpen, setIsOpen] = useState(false);
  const { parsedMoment, parsedDateString } = getMomentAndDateString(
    momentFormat,
    currentDate,
    keepInvalidSuggestion,
    !nullable
  );
  const [dateTimeString, setDateTimeString] = useState(parsedDateString);
  const [dateTime, setDateTime] = useState<moment.Moment | undefined>(parsedMoment);
  const [suggestion, setSuggestion] = useState<ValidateDateResult>();
  const inputRef: MutableRefObject<HTMLInputElement | null> = useRef(null);
  const prevDate = useRef<string | undefined>(parsedDateString);

  const nonNullableFieldReset = useMemo(
    () =>
      debounce((value: string) => {
        setDateTimeString(value);
      }, 700),
    []
  );

  const validate = useCallback(
    async (dateText: string, currentDate?: moment.Moment) => {
      const lastDateText = getFormattedDateString(currentDate, format);
      const valSuggestion = await validateDate(dateText, lastDateText, format);
      onSuggestionChange && onSuggestionChange(dateText, valSuggestion);
      setSuggestion(valSuggestion);
      if (!valSuggestion && !nullable && !dateText) {
        nonNullableFieldReset(lastDateText);
      }
    },
    [format, onSuggestionChange, nullable, nonNullableFieldReset]
  );

  const debouncedDateTextCheck = useMemo(() => debounce(validate, 300), [validate]);

  useEffect(() => {
    setDateTimeString(parsedDateString);
    setDateTime(parsedMoment);
    prevDate.current = parsedDateString;
    // we want this to run only if the string value coming in is different:
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedDateString]);

  const onFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateTimeString(e.target.value);
    prevDate.current = e.target.value;
    debouncedDateTextCheck.clear();
    nonNullableFieldReset.clear();
    if (isNullOrWhitespace(e.target.value)) {
      onClear && onClear();
      debouncedDateTextCheck(e.target.value, dateTime);
    } else {
      if (isValidDateTime(e.target.value, momentFormat, nullable, true)) {
        const momentValue = getMomentDateObject(e.target.value, momentFormat);
        setFieldValue(momentValue);
        setDateTime(momentValue);
        onChange(momentValue);
        setSuggestion(undefined);
      } else {
        debouncedDateTextCheck(e.target.value, dateTime);
      }
    }
  };

  const onDateChange = (date?: moment.Moment) => {
    textBoxRef !== undefined ? textBoxRef.current?.focus() : inputRef.current?.focus();
    //Preserve previously selected time value while changing date from calendar
    const hours = dateTime?.get('hours') || 0;
    const minutes = dateTime?.get('minutes') || 0;
    date?.set('hours', hours);
    date?.set('minutes', minutes);
    setDateTime(date);
    setDateTimeString(date?.format(momentFormat) || '');
  };

  const close = () => {
    setSuggestion(undefined);
    setIsOpen(false);
  };

  const setFieldValue = (date?: moment.Moment) => {
    setDateTimeString(getFormattedDateString(date, format));
  };

  const acceptSuggestion = () => {
    if (!suggestion) {
      return;
    } else {
      if (suggestion.valid) {
        // save value
        const momentValue = getMomentDateObject(suggestion?.date, momentFormat);
        // update local state
        setFieldValue(momentValue);
        setDateTime(momentValue);
        // dispatch change
        onChange(momentValue);
      } else {
        // put it back only if invalid suggestion is meant to be auto currect
        if (!keepInvalidSuggestion) setFieldValue(dateTime);
      }
      setSuggestion(undefined);
      setIsOpen(false);
    }
  };

  const keyHandler = (e: React.KeyboardEvent) => {
    e.preventDefault();
    switch (e.key) {
      case 'Enter':
        acceptSuggestion();
        break;
      case 'Escape':
        close();
        break;
      default:
        break;
    }
  };

  const open = isOpen || !!suggestion;
  const onClose = useCallback(() => {
    setIsOpen(false);
    let ref = textBoxRef !== undefined ? textBoxRef : inputRef;
    if (prevDate.current !== dateTimeString) {
      if (isNullOrWhitespace(dateTimeString)) {
        onClear && onClear();
        ref.current && ref.current.focus();
        return;
      }
      if (suggestion && suggestion.valid === false) {
        return setDateTimeString(suggestion.date);
      }
      setSuggestion(undefined);
      onChange(dateTime);
    }
    ref.current && ref.current.focus();
  }, [dateTime, onChange, suggestion, textBoxRef, onClear, dateTimeString]);

  const onIconClick = useCallback(() => {
    if (isOpen) {
      // same close and submit value behavior if it's already open:
      onClose();
    } else {
      // open it:
      setSuggestion(undefined);
      setIsOpen(true);
    }
  }, [isOpen, onClose]);

  const wrapperRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const tabIndex = useTabbingContext(propsTabIndex);
  const { isFocused, focusTrackingProps } = useTrackFocus({
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
      acceptSuggestion();
      if (onExit) onExit(event);
    },
    onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
      if (onEnter) onEnter(event);
    },
  });

  const IconButtonView = useMemo(() => {
    if (hideIcon) return null;
    const icon = (
      <IconButton
        className={classNames('dpc-combo-box__arrow-down', iconButtonClass)}
        onClick={onIconClick}
        title={'Open'}
        iconClassName={getIconClassName(isOpen, isDateTimePicker, iconClass)}
        iconSize="input"
        tabIndex={-1} // we don't want this to receive tab-focus... until we have to do an accessibility overhaul.
      />
    );
    if (useRippleEffect) return <Ripple isIconButton={true}>{icon}</Ripple>;
    return icon;
  }, [hideIcon, iconButtonClass, iconClass, isDateTimePicker, isOpen, onIconClick, useRippleEffect]);

  return (
    <ValidationWrapper validation={validation} shouldShow={!isOpen}>
      <div className={getClasses(false, isFocused, className)} ref={wrapperRef}>
        <input
          autoComplete="off"
          autoFocus={autoFocus}
          type="text"
          className={getInputClassNames(inputClassName)}
          placeholder={momentFormat}
          value={dateTimeString}
          onChange={onFieldChange}
          ref={textBoxRef !== undefined ? textBoxRef : inputRef}
          tabIndex={tabIndex}
          onKeyUp={keyHandler}
          {...focusTrackingProps}
        />

        {IconButtonView}
        {
          <DropDownOverlay
            sourceRef={holderRef !== undefined ? holderRef : wrapperRef}
            isOpen={open}
            onClose={onClose}
            placement={'bottom'}
            callOutMinWidth={260}
            callOutWidth={suggestion ? undefined : 362}
            callOutMinHeight={suggestion ? undefined : isDateTimePicker ? 450 : 335}
            callOutMaxHeight="auto"
            className={pickerClassname}
            flipBehavior={['bottom', 'top', 'right', 'left']}
          >
            {suggestion ? (
              <DropDownOption index={0} isFocused={true} onChosen={acceptSuggestion}>
                {suggestion.suggestion}
              </DropDownOption>
            ) : (
              <>
                <Calendar onChange={onDateChange} currentDate={dateTime} hideClear={!nullable} />
                {isDateTimePicker && (
                  <TimeSliders
                    hours={dateTime?.get('hours') || 0}
                    minutes={dateTime?.get('minutes') || 0}
                    onChange={({ hours, minutes }: ITimeSliderValue) => {
                      const newMoment = getMoment()(dateTime);
                      newMoment.set('hours', hours);
                      newMoment.set('minutes', minutes);
                      setDateTime(newMoment);
                      setFieldValue(newMoment);
                    }}
                  />
                )}
              </>
            )}
          </DropDownOverlay>
        }
      </div>
    </ValidationWrapper>
  );
}
